import {
  EditorConfig,
  OutputData,
  ToolConstructable,
  ToolSettings,
} from '@editorjs/editorjs';

import { useEditorClipboard } from '@/data/hook/editorjs/useEditorClipboard';
import { useEditorJS } from '@/data/hook/editorjs/useEditorJS';
import { useEditorJSTools } from '@/data/hook/editorjs/useEditorTools';
import { ImageFolderEnum } from '@/models/CkEditor';
import { parseHtmlStringToOutputData } from '@/utils/editorjs/parse';
import { useState, useCallback } from 'react';
import { twMerge } from 'tailwind-merge';
import { delay } from '@/utils/delay';

type ReadOnly = {
  readOnly: true;
  folder?: undefined;
};

type ToolObject = {
  [toolName: string]: ToolSettings | ToolConstructable;
};

type Writable = {
  readOnly?: false;
  folder: ImageFolderEnum;
};
export type EditorProps = {
  value?: OutputData | { htmlContent: string };
  onChange?: (value: OutputData) => void;
  extraBody?: object;
  activityBlock?: ToolSettings;
  codeEditorBlock?: ToolSettings;
  imageGeneratorBlock?: ToolSettings;
} & Omit<EditorConfig, 'onChange' | 'data' | 'tools'> &
  (Writable | ReadOnly);

export default function EditorJs({
  value,
  onChange,
  folder,
  extraBody,
  activityBlock,
  imageGeneratorBlock,
  readOnly,
  codeEditorBlock,
  ...editorProps
}: EditorProps) {
  const tools = useEditorJSTools({
    folder,
    extraBody,
    readOnly,
  });

  const mergeTools = (base: ToolObject, extra: Partial<ToolObject>) => {
    const tools = base;
    for (const key in extra) {
      if (extra[key]) {
        tools[key] = extra[key]!;
      }
    }
    return tools;
  };

  const { setElement, element, instance } = useEditorJS({
    data: tranformValue(value),
    onChange,
    tools: mergeTools(tools, {
      activityBlock,
      code: codeEditorBlock,
      imageGenerator: imageGeneratorBlock,
    }),
    readOnly,
    ...editorProps,
  });

  useEditorClipboard({
    editorInstance: instance,
    folder: folder ?? ImageFolderEnum.BOOK,
    extraBody,
    readOnly,
    element,
    onChange,
  });

  const [openedToolbar, setOpenedToolbar] = useState(false);

  const checkToolbarOpened = useCallback(async () => {
    await delay(50);
    const toolbar1 = element?.querySelector('.codex-editor--toolbox-opened');
    const toolbar2 = element?.querySelector('.ce-popover--opened');

    setOpenedToolbar(!!toolbar1 || !!toolbar2);
  }, [element]);

  return (
    <div
      className={twMerge(
        'relative ml-auto text-left',
        !readOnly ? 'w-full sm:w-[calc(100%-60px)] ' : 'w-full',
        openedToolbar ? 'z-10' : 'z-0',
      )}
      onClick={e => {
        e.stopPropagation();
        checkToolbarOpened();
      }}
      onFocus={checkToolbarOpened}
      onMouseLeave={checkToolbarOpened}
    >
      <div ref={ref => setElement(ref)} />
    </div>
  );
}

const tranformValue = (value?: OutputData | { htmlContent: string }) => {
  if (value && 'htmlContent' in value) {
    return parseHtmlStringToOutputData(value.htmlContent);
  }
  return value;
};
